import type { MessageKey } from '@dijitalapp/i18n';
import type { AdminSiteDto, SiteContent, SiteLocale } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { hasMessage } from '@dijitalapp/i18n';

/*
 * Admin: dijitalapp.com content. The form is generated from the content's own shape, so a new field
 * in the site schema shows up here without UI work. Saving publishes at once and keeps a version
 * that can be restored; two admins editing at the same time get a conflict instead of an overwrite.
 */

type Json = string | number | boolean | null | Json[] | { [k: string]: Json };
type Path = (string | number)[];

const SECTIONS: { key: string; fields: (keyof SiteContent)[] }[] = [
  { key: 'general', fields: ['meta', 'labels', 'nav'] },
  { key: 'hero', fields: ['hero'] },
  { key: 'product', fields: ['product'] },
  { key: 'security', fields: ['security'] },
  { key: 'business', fields: ['business'] },
  { key: 'plus', fields: ['plus'] },
  { key: 'plans', fields: ['plans'] },
  { key: 'apps', fields: ['apps'] },
  { key: 'faq', fields: ['faq'] },
  { key: 'final', fields: ['final', 'footer'] },
];
const ENUMS: Record<string, string[]> = {
  visual: ['chat', 'spaces', 'learn', 'calendar'],
  status: ['available', 'soon'],
  kind: ['web', 'pwa', 'ios', 'android', 'huawei'],
};
const LONG = new Set(['lead', 'body', 'a', 'description', 'demoAnswer']);

function setAt(root: Json, path: Path, value: Json): Json {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(root)) {
    const copy = root.slice();
    copy[head as number] = setAt(copy[head as number] as Json, rest, value);
    return copy;
  }
  const obj = root as { [k: string]: Json };
  return { ...obj, [head as string]: setAt(obj[head as string] as Json, rest, value) };
}
/** An empty item shaped like the first default item (for "add"). */
function blank(sample: Json): Json {
  if (typeof sample === 'string') return '';
  if (typeof sample === 'boolean') return sample;
  if (Array.isArray(sample)) return [];
  if (sample && typeof sample === 'object') return Object.fromEntries(Object.entries(sample).map(([k, v]) => [k, ENUMS[k] ? v : blank(v)]));
  return sample;
}

export function SiteEditor() {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const [locale, setLocale] = useState<SiteLocale>('tr');
  const [data, setData] = useState<AdminSiteDto | null>(null);
  const [draft, setDraft] = useState<SiteContent | null>(null);
  const [section, setSection] = useState('general');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const label = (key: string) => {
    const k = `siteField.${key}`;
    return hasMessage(k) ? t(k as MessageKey) : key;
  };

  useEffect(() => {
    let alive = true;
    void api<AdminSiteDto>('GET', `/admin/site/${locale}`).then((d) => {
      if (!alive) return;
      setData(d);
      setDraft(d.content);
      setStatus(null);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  if (!data || !draft) return <p className="muted">{t('app.loading')}</p>;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.content);
  const siteUrl = `https://dijitalapp.com${locale === 'tr' ? '/' : '/en/'}`;

  const change = (path: Path, value: Json) => setDraft((d) => setAt(d as unknown as Json, path, value) as unknown as SiteContent);
  const run = (fn: () => Promise<AdminSiteDto>, okText: string) => {
    setBusy(true);
    setStatus(null);
    fn()
      .then(
        (d) => {
          setData(d);
          setDraft(d.content);
          setStatus({ ok: true, text: okText });
        },
        (err: unknown) => setStatus({ ok: false, text: t(errorMessageKey(err)) }),
      )
      .finally(() => setBusy(false));
  };
  const save = () => run(() => api('PUT', `/admin/site/${locale}`, { content: draft, expectedVersion: data.version }), t('adminOps.site.saved'));
  const restore = (historyId: string) =>
    run(() => api('POST', `/admin/site/${locale}/restore`, { historyId, expectedVersion: data.version }), t('adminOps.site.restored'));
  const resetSection = () => {
    const fields = SECTIONS.find((s) => s.key === section)!.fields;
    setDraft((d) => ({ ...d!, ...Object.fromEntries(fields.map((f) => [f, data.defaults[f]])) }));
  };

  /** One field, by the shape of its value. */
  const field = (key: string, value: Json, path: Path, sample: Json): React.ReactNode => {
    const id = `site-${path.join('-')}`;
    if (typeof value === 'boolean') {
      return (
        <label key={id} className="check">
          <input type="checkbox" checked={value} onChange={(e) => change(path, e.target.checked)} />
          <span>{label(key)}</span>
        </label>
      );
    }
    if (typeof value === 'string') {
      const options = ENUMS[key];
      return (
        <div key={id} className="field">
          <label htmlFor={id}>{label(key)}</label>
          {options ? (
            <select id={id} className="input" value={value} onChange={(e) => change(path, e.target.value)}>
              {options.map((o) => (
                <option key={o} value={o}>
                  {label(`${key}.${o}`)}
                </option>
              ))}
            </select>
          ) : LONG.has(key) ? (
            <textarea id={id} className="input" rows={3} value={value} onChange={(e) => change(path, e.target.value)} />
          ) : (
            <input id={id} className="input" value={value} onChange={(e) => change(path, e.target.value)} />
          )}
        </div>
      );
    }
    if (Array.isArray(value)) {
      const itemSample = (Array.isArray(sample) && sample[0]) ?? value[0] ?? '';
      const move = (from: number, to: number) => {
        if (to < 0 || to >= value.length) return;
        const copy = value.slice();
        const [it] = copy.splice(from, 1);
        copy.splice(to, 0, it!);
        change(path, copy);
      };
      return (
        <fieldset key={id} className="site-list">
          <legend>{label(key)}</legend>
          {value.map((item, i) => (
            <div key={i} className={typeof item === 'object' ? 'site-item card-inset' : 'site-item site-item-inline'}>
              <div className="site-item-tools">
                {typeof item === 'object' && <span className="muted">#{i + 1}</span>}
                <span className="spacer" />
                <button type="button" className="btn btn-sm" aria-label={t('adminOps.site.up')} disabled={i === 0} onClick={() => move(i, i - 1)}>
                  ↑
                </button>
                <button type="button" className="btn btn-sm" aria-label={t('adminOps.site.down')} disabled={i === value.length - 1} onClick={() => move(i, i + 1)}>
                  ↓
                </button>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => change(path, value.filter((_, j) => j !== i))}>
                  {t('adminOps.site.remove')}
                </button>
              </div>
              {typeof item === 'object' && item !== null && !Array.isArray(item)
                ? Object.entries(item).map(([k, v]) =>
                    field(k, v, [...path, i, k], (itemSample as { [k: string]: Json } | null)?.[k] ?? v),
                  )
                : field('item', item, [...path, i], itemSample)}
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={() => change(path, [...value, blank(itemSample)])}>
            + {t('adminOps.site.add')}
          </button>
        </fieldset>
      );
    }
    if (value && typeof value === 'object') {
      return (
        <fieldset key={id} className="site-group">
          <legend>{label(key)}</legend>
          {Object.entries(value).map(([k, v]) => field(k, v, [...path, k], (sample as { [k: string]: Json } | null)?.[k] ?? v))}
        </fieldset>
      );
    }
    return null;
  };

  const current = SECTIONS.find((s) => s.key === section)!;
  return (
    <div className="site-editor">
      <div className="card site-bar">
        <div className="segmented" role="radiogroup" aria-label={t('adminOps.site.language')}>
          {(['tr', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={locale === l}
              disabled={busy}
              onClick={() => {
                if (dirty && !window.confirm(t('adminOps.site.discard'))) return;
                setLocale(l);
              }}
            >
              {l === 'tr' ? 'Türkçe' : 'English'}
            </button>
          ))}
        </div>
        <span className="muted site-version">
          {data.version
            ? t('adminOps.site.version', { version: data.version, by: data.updatedBy ? `@${data.updatedBy}` : '—', date: formatDate(data.updatedAt!, { dateStyle: 'medium', timeStyle: 'short' }) })
            : t('adminOps.site.defaults')}
        </span>
        <span className="spacer" />
        <a className="btn btn-sm" href={siteUrl} target="_blank" rel="noopener">
          {t('adminOps.site.open')}
        </a>
        <button type="button" className="btn btn-sm" disabled={!dirty || busy} onClick={() => setDraft(data.content)}>
          {t('adminOps.site.undo')}
        </button>
        <button type="button" className="btn btn-sm btn-primary" disabled={!dirty || busy} onClick={save}>
          {t('adminOps.site.save')}
        </button>
      </div>
      {status && (
        <p role="status" className={status.ok ? 'alert alert-info' : 'alert alert-error'}>
          {status.text}
        </p>
      )}
      <div className="site-layout">
        <nav className="site-sections" aria-label={t('adminOps.site.sections')}>
          {SECTIONS.map((s) => (
            <button key={s.key} type="button" className={s.key === section ? 'is-on' : ''} aria-current={s.key === section ? 'true' : undefined} onClick={() => setSection(s.key)}>
              {t(`adminOps.site.section.${s.key}` as MessageKey)}
            </button>
          ))}
        </nav>
        <section className="card stack site-form" aria-label={t(`adminOps.site.section.${section}` as MessageKey)}>
          {current.fields.map((f) => field(f, draft[f] as unknown as Json, [f], data.defaults[f] as unknown as Json))}
          <div className="row">
            <button type="button" className="btn btn-sm" onClick={resetSection}>
              {t('adminOps.site.resetSection')}
            </button>
          </div>
        </section>
        <aside className="card stack site-history">
          <h2 className="card-title">{t('adminOps.site.history')}</h2>
          {data.history.length === 0 && <p className="muted">{t('adminOps.site.noHistory')}</p>}
          {data.history.map((h) => (
            <div key={h.id} className="site-history-row">
              <div>
                <strong>v{h.version}</strong>
                <p className="muted">
                  {formatDate(h.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  {h.createdBy ? ` · @${h.createdBy}` : ''}
                </p>
              </div>
              {h.version !== data.version && (
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => restore(h.id)}>
                  {t('adminOps.site.restore')}
                </button>
              )}
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
